import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { getAuthHeaders } from '../../context/AuthContext';
import { Users, CalendarCheck, Layers, CreditCard, ArrowRight, IndianRupee, Eye, EyeOff, Cake } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFinancial } from '../../context/FinancialContext';
import FinancialVerifyModal from '../../components/FinancialVerifyModal';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtBirthday(nextBirthday, daysUntil) {
  const d = new Date(nextBirthday + 'T00:00:00');
  const dd = d.getDate();
  const mon = MONTHS[d.getMonth()];
  if (daysUntil === 0) return `${dd} ${mon} — Today 🎉`;
  if (daysUntil === 1) return `${dd} ${mon} — Tomorrow`;
  return `${dd} ${mon} (${DAYS[d.getDay()]})`;
}

function BirthdayWidget({ birthdays }) {
  if (!birthdays || birthdays.length === 0) return null;
  return (
    <div>
      <h2 className="font-heading text-lg font-medium text-[#292524] mb-3 flex items-center gap-2">
        <Cake className="w-5 h-5 text-[#F59E0B]" strokeWidth={1.5} />
        Student Birthdays in the Next 7 Days
      </h2>
      <div className="bg-white rounded-3xl shadow-card border border-[#E7E5E4]/50 overflow-hidden">
        {birthdays.map((b, i) => (
          <div
            key={b.student_id}
            className={`flex items-center justify-between px-5 py-4 gap-4
              ${i !== birthdays.length - 1 ? 'border-b border-[#E7E5E4]/60' : ''}
              ${i === 0 ? 'bg-amber-50/60 border-l-4 border-l-[#F59E0B]' : ''}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#FFEDD5] rounded-2xl flex items-center justify-center font-heading font-semibold text-sm text-[#D97706] shrink-0">
                {b.student_name?.[0]?.toUpperCase()}
              </div>
              <div>
                <p className="font-heading font-medium text-[#292524] text-sm">{b.student_name}</p>
                <p className="text-xs text-[#78716C] font-body mt-0.5">{fmtBirthday(b.next_birthday, b.days_until)}</p>
              </div>
            </div>
            {i === 0 && (
              <span className="text-xs font-heading font-semibold bg-[#F59E0B] text-white rounded-full px-3 py-1 shrink-0">
                Next Birthday
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [birthdays, setBirthdays] = useState([]);
  const { revealed, hide } = useFinancial();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/dashboard-stats`, { headers: getAuthHeaders() })
      .then(r => setStats(r.data))
      .catch(console.error);
    axios.get(`${API}/api/admin/upcoming-birthdays`, { headers: getAuthHeaders() })
      .then(r => setBirthdays(r.data || []))
      .catch(console.error);
  }, []);

  const collectionValue = stats ? `₹${stats.monthly_collection.toLocaleString('en-IN')}` : '—';

  const STATS = [
    { label: 'Active Students', value: stats ? stats.active_students : '—', icon: Users, color: 'bg-amber-50 text-amber-600', path: '/admin/students' },
    { label: 'Present Today', value: stats ? (stats.today_is_holiday ? 'Holiday' : stats.today_present) : '—', icon: CalendarCheck, color: 'bg-green-50 text-green-600', path: '/admin/attendance' },
    { label: 'Pending Payments', value: stats ? stats.pending_payments : '—', icon: CreditCard, color: 'bg-red-50 text-red-500', path: '/admin/payments' },
  ];

  return (
    <div className="space-y-8 animate-fade-in-up">
      {showModal && <FinancialVerifyModal onClose={() => setShowModal(false)} />}

      {/* Greeting */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-1">Welcome back</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#292524]">
          Hello, {user?.name || 'Meena'} 👋
        </h1>
        <p className="text-[#57534E] font-body mt-1">Here's a quick look at your fitness community today.</p>
      </div>

      {/* Stats grid — Monthly Collection is separate for masking */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STATS.map((stat, i) => (
          <button key={stat.label} onClick={() => navigate(stat.path)}
            data-testid={`admin-stat-card-${i}`}
            className="bg-white rounded-3xl p-5 shadow-card border border-[#E7E5E4]/50 hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-300 text-left group">
            <div className={`inline-flex p-2.5 rounded-2xl mb-3 ${stat.color}`}>
              <stat.icon className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <p className="text-2xl font-heading font-semibold text-[#292524]">{stat.value}</p>
            <p className="text-xs text-[#78716C] font-body mt-0.5">{stat.label}</p>
          </button>
        ))}

        {/* Monthly Collection — protected card */}
        <div
          data-testid="admin-stat-card-collection"
          onClick={() => navigate('/admin/payments')}
          className="bg-white rounded-3xl p-5 shadow-card border border-[#E7E5E4]/50 hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-300 text-left cursor-pointer relative group"
        >
          <div className="inline-flex p-2.5 rounded-2xl mb-3 bg-emerald-50 text-emerald-600">
            <IndianRupee className="w-5 h-5" strokeWidth={1.5} />
          </div>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-heading font-semibold text-[#292524]">
              {revealed ? collectionValue : '₹ ••••••'}
            </p>
            <button
              data-testid="toggle-collection-visibility"
              onClick={(e) => { e.stopPropagation(); if (revealed) hide(); else setShowModal(true); }}
              className="text-[#A8A29E] hover:text-[#F59E0B] transition-colors p-0.5"
              title={revealed ? 'Hide amount' : 'Show amount'}
            >
              {revealed ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
            </button>
          </div>
          <p className="text-xs text-[#78716C] font-body mt-0.5">Monthly Collection</p>
        </div>
      </div>

      {/* Birthday widget — only rendered when there are upcoming birthdays */}
      <BirthdayWidget birthdays={birthdays} />

      {/* Quick actions */}
      <div>
        <h2 className="font-heading text-lg font-medium text-[#292524] mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { label: 'Add New Student', icon: Users, path: '/admin/students', desc: 'Register a new student to the class' },
            { label: 'Mark Attendance', icon: CalendarCheck, path: '/admin/attendance', desc: "Record today's attendance" },
            { label: 'Manage Batches', icon: Layers, path: '/admin/batches', desc: 'Create or edit class time slots' },
          ].map(action => (
            <button key={action.label} onClick={() => navigate(action.path)}
              data-testid={`admin-quick-action-${action.label.replace(/\s+/g, '-').toLowerCase()}`}
              className="bg-white rounded-3xl p-6 shadow-card border border-[#E7E5E4]/50 hover:shadow-card-hover hover:border-[#F59E0B]/30 transition-all duration-300 text-left group flex items-start gap-4">
              <div className="bg-[#FFEDD5] p-3 rounded-2xl shrink-0 group-hover:bg-[#F59E0B] transition-colors">
                <action.icon className="w-5 h-5 text-[#D97706] group-hover:text-white transition-colors" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <p className="font-heading font-medium text-[#292524] text-sm">{action.label}</p>
                <p className="text-xs text-[#78716C] font-body mt-0.5">{action.desc}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-[#A8A29E] group-hover:text-[#F59E0B] transition-colors shrink-0 mt-0.5" strokeWidth={1.5} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
